import { useMemo, useState } from "react";

import { type Building, type Module } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { ActionButton, Alert, Field, Heading, Loader, toast, Typography } from "@eisox/design-system";
import { isFeatureSupported } from "@eisox/device-features";
import { useAppForm } from "@eisox/form";
import { UpdateIcon } from "@eisox/icons";
import { useSelector } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { moduleApi } from "~/api";
import { DeleteDialog } from "~/components/delete-dialog";
import { Tooltip } from "~/components/tooltip";
import { VersionGate } from "~/components/version-gate";
import { LiveErrorDialog } from "~/features/real-time";
import { getInitialState, Mode } from "~/features/settings/helpers/building-season-settings.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import type { Permission } from "~/types/auth.type";
import type { EnhancedGateway } from "~/types/gateway.type";

import {
  BOILER_ROOM_MODULE_TYPES,
  formToMaintenanceAction,
  formToUpdateBody,
  getModuleStatusLabel,
  getModuleStatusSeverity,
  getModuleTypeLabel,
  isBacnetModule,
  isNiagara,
  isPLCMaintenanceEnabled,
  MODULE_STATUS_POLL_INTERVAL_MS,
  moduleSettingsSchema,
  moduleToFormValues,
  PLC_MAINTENANCE_DURATIONS,
} from "../helpers";

const NAME = "ModuleSettings";

interface ModuleSettingsProps {
  buildingId: string;
  module: Module;
  /** All of the building's modules — used for the mid-season "last boiler-room module" delete rule. */
  modules: Module[];
  gateways: EnhancedGateway[];
  building: Building;
  permissions: Permission;
  /** Called after a successful delete so the parent can navigate back to the module list. */
  onDeleted: () => void;
}

export function ModuleSettings({
  buildingId,
  module,
  modules,
  gateways,
  building,
  permissions,
  onDeleted,
}: ModuleSettingsProps) {
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { boilerRooms, airHandlerUnits } = useBuildingContext(NAME);

  const gatewayOptions = useMemo(() => gateways.map(g => ({ value: g.id, name: g.name })), [gateways]);
  const boilerRoom = boilerRooms.find(br => br.moduleId === module.id);
  const airHandlerUnit = airHandlerUnits.find(ahu => ahu.moduleId === module.id);

  const moduleGateway = gateways.find(g => g.id === module.gatewayId);

  const { data: moduleStatus } = useQuery({
    ...moduleApi.queries.status(buildingId, module.id),
    refetchInterval: MODULE_STATUS_POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
    meta: { skipGlobalLoader: true },
  });

  const statusLabel = getModuleStatusLabel(moduleStatus);

  const form = useAppForm({
    defaultValues: moduleToFormValues(module),
    validators: { onSubmit: moduleSettingsSchema(module) },
    onSubmit: async ({ formApi, value }) => {
      const maintenance = formToMaintenanceAction(value, module);

      await moduleApi.update(buildingId, module.id, formToUpdateBody(value, module));
      if (maintenance) await moduleApi.setMaintenance(buildingId, module.id, maintenance);

      await queryClient.invalidateQueries(moduleApi.queries.building(buildingId));
      if (maintenance) await queryClient.invalidateQueries(moduleApi.queries.maintenanceLink(buildingId, module.id));

      toast.success(m.module_settings_updated());
      // Clear write-only fields and rebase the form so it is no longer dirty.
      formApi.reset({
        ...value,
        userName: "",
        password: "",
        proxy: { ...value.proxy, userName: "", password: "" },
        plcMaintenanceDuration: "",
      });
    },
  });

  const proxyEnabled = useSelector(form.store, state => state.values.proxy.isEnabled);
  const plcMaintenanceEnabled = useSelector(form.store, state => state.values.plcMaintenanceEnabled);
  const {
    data: maintenanceLink,
    isLoading: isMaintenanceLinkLoading,
    refetch: refetchMaintenanceLink,
  } = useQuery({
    ...moduleApi.queries.maintenanceLink(buildingId, module.id),
    enabled: plcMaintenanceEnabled,
    retry: false,
  });

  const canUpdate = permissions.module?.update;

  const boilerRoomModuleCount = modules.filter(md => BOILER_ROOM_MODULE_TYPES.includes(md.type)).length;
  const isHalfSeasonMode = getInitialState(building).mode === Mode.SUMMER_WINTER_WITH_HALF_SEASON;
  const deletionDisabled = isHalfSeasonMode && boilerRoomModuleCount <= 1;

  const handleDelete = async () => {
    await moduleApi.delete(buildingId, module.id);
    await queryClient.invalidateQueries(moduleApi.queries.building(buildingId));
    onDeleted();
  };

  return (
    <form
      className="min-h-0 gap-5 flex h-full flex-col text-base"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <Alert className="w-full" severity={getModuleStatusSeverity(moduleStatus)}>
        <div className="gap-1.25 flex flex-col">
          {statusLabel && <Typography>{statusLabel}</Typography>}
          {moduleGateway?.lastGatewayActivity && (
            <Typography>
              {m.module_settings_status_last_communication({
                date: dayjs(moduleGateway.lastGatewayActivity).format("L - LT"),
              })}
            </Typography>
          )}
        </div>
        <LiveErrorDialog
          errors={moduleStatus?.errors ?? []}
          module={module}
          boilerRoom={boilerRoom}
          airHandlerUnit={airHandlerUnit}
        >
          <ActionButton className="ml-auto bg-primary-white text-primary-dark-blue">
            {m.module_settings_debug()}
          </ActionButton>
        </LiveErrorDialog>
      </Alert>
      <form.AppForm>
        <div className="gap-10 flex flex-wrap overflow-y-auto">
          <div className="gap-1.25 flex flex-[1_1_250px] flex-col">
            <Field.Root>
              <Field.Label>{m.module_settings_type()}</Field.Label>
              <p>{getModuleTypeLabel(module.type)}</p>
            </Field.Root>
            <form.AppField name="gatewayId">
              {field => (
                <field.Select
                  label={m.module_settings_gateway()}
                  multiple={false}
                  options={gatewayOptions}
                  disabled={!canUpdate}
                  classNames={{ control: "w-full" }}
                  onValueChange={value => {
                    if (typeof value !== "string") return;
                    const selectedGateway = gateways.find(g => g.id === value);
                    if (
                      selectedGateway &&
                      !isFeatureSupported("gateway", "BOILERROOM_BACNET_DEVICE_ID", selectedGateway.softwareVersion)
                    ) {
                      form.setFieldValue("bacnetDeviceId", undefined);
                    }
                  }}
                />
              )}
            </form.AppField>
            {isBacnetModule(module) ? (
              <VersionGate
                devices={[
                  {
                    type: "gateway",
                    current: moduleGateway?.softwareVersion,
                    feature: "BOILERROOM_BACNET_DEVICE_ID",
                  },
                ]}
              >
                {({ disabled: bacnetVersionBlocked }) => (
                  <form.AppField name="bacnetDeviceId">
                    {field => (
                      <field.NumberField
                        classNames={{ control: "w-full" }}
                        label={m.module_settings_bacnet()}
                        disabled={!canUpdate || bacnetVersionBlocked}
                      />
                    )}
                  </form.AppField>
                )}
              </VersionGate>
            ) : (
              <form.AppField name="url">
                {field => (
                  <field.TextField
                    classNames={{ control: "w-full" }}
                    label={m.module_settings_ip()}
                    placeholder={m.module_settings_ip_placeholder()}
                    disabled={!canUpdate}
                  />
                )}
              </form.AppField>
            )}
            <form.AppField name="userName">
              {field => (
                <field.TextField
                  classNames={{ control: "w-full" }}
                  label={m.module_settings_user()}
                  autoComplete="new-password"
                  placeholder="*****"
                  disabled={!canUpdate}
                />
              )}
            </form.AppField>
            <form.AppField name="password">
              {field => (
                <field.PasswordField
                  classNames={{ control: "w-full" }}
                  label={m.module_settings_password()}
                  autoComplete="new-password"
                  placeholder="*****"
                  disabled={!canUpdate}
                />
              )}
            </form.AppField>
          </div>

          <div className="gap-1.25 flex flex-[1_1_250px] flex-col">
            <Heading as="h2" size={2} className="text-primary-blue">
              {m.module_settings_gtb()}
            </Heading>
            <form.AppField name="proxy.isEnabled">{field => <field.Switch disabled={!canUpdate} />}</form.AppField>
            {proxyEnabled && (
              <>
                <form.AppField name="proxy.name">
                  {field => (
                    <field.TextField
                      classNames={{ control: "w-full" }}
                      label={m.module_settings_gtb_name()}
                      placeholder={m.module_settings_gtb_name_placeholder()}
                      disabled={!canUpdate}
                    />
                  )}
                </form.AppField>
                <form.AppField name="proxy.userName">
                  {field => (
                    <field.TextField
                      classNames={{ control: "w-full" }}
                      label={m.module_settings_gtb_user()}
                      autoComplete="new-password"
                      placeholder={module.proxy ? "*****" : m.module_settings_gtb_user_placeholder()}
                      disabled={!canUpdate}
                    />
                  )}
                </form.AppField>
                <form.AppField name="proxy.password">
                  {field => (
                    <field.PasswordField
                      classNames={{ control: "w-full" }}
                      label={m.module_settings_gtb_password()}
                      autoComplete="new-password"
                      placeholder={module.proxy ? "*****" : m.module_settings_gtb_password_placeholder()}
                      disabled={!canUpdate}
                    />
                  )}
                </form.AppField>
                {isNiagara(module.type) && (
                  <label className="flex items-center justify-between">
                    <span className="font-medium text-primary-dark-blue">{m.bms_open_in_new_tab()}</span>
                    <form.AppField name="proxy.isExternal">
                      {field => <field.Switch disabled={!canUpdate} />}
                    </form.AppField>
                  </label>
                )}
              </>
            )}

            <Heading as="h2" size={2} className="text-primary-blue">
              {m.module_settings_plc_maintenance()}
            </Heading>
            <form.AppField name="plcMaintenanceEnabled">
              {field => <field.Switch disabled={!canUpdate} />}
            </form.AppField>
            {plcMaintenanceEnabled && (
              <>
                <form.AppField name="plcMaintenanceDuration">
                  {field => (
                    <field.Select
                      label={m.module_settings_plc_maintenance_duration()}
                      multiple={false}
                      options={PLC_MAINTENANCE_DURATIONS}
                      placeholder="---"
                      disabled={!canUpdate}
                      classNames={{ control: "w-full" }}
                    />
                  )}
                </form.AppField>
                {module.sshOperatorExpireAt && <p>{dayjs(module.sshOperatorExpireAt).format("L - LTS")}</p>}
                <div className="font-medium gap-1.25 flex items-center italic">
                  {m.module_settings_plc_maintenance_link()}
                  {isMaintenanceLinkLoading && <Loader size={10} />}
                  {maintenanceLink?.url ? (
                    <Link to={maintenanceLink.url} target="_blank" rel="noopener noreferrer">
                      {maintenanceLink.url}
                    </Link>
                  ) : (
                    isPLCMaintenanceEnabled(module) && (
                      <>
                        <span>{m.module_settings_plc_maintenance_not_generated()}</span>
                        {/* The tunnel takes a moment to come up after enabling — let the user re-poll. */}
                        <UpdateIcon className="h-3 w-3 cursor-pointer" onClick={() => void refetchMaintenanceLink()} />
                      </>
                    )
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="gap-2.5 mt-auto flex items-center justify-center">
          {permissions.module?.delete && (
            <>
              <Tooltip content={m.module_settings_delete_tooltip()} openingCondition={deletionDisabled}>
                <ActionButton variant="danger" disabled={deletionDisabled} onClick={() => setDeleteOpen(true)}>
                  {m.delete()}
                </ActionButton>
              </Tooltip>
              <DeleteDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                onDelete={() => void handleDelete()}
                description={m.module_settings_delete_title({
                  name: module.proxy?.name ?? getModuleTypeLabel(module.type),
                })}
              />
            </>
          )}
          <form.SubmitButton label={m.save()} />
        </div>
      </form.AppForm>
    </form>
  );
}
